import React, { useState, useEffect } from "react";

/**
 * Returns a Date object that represents the same wall-clock time in the given timeZone.
 * Example: getTzDate('Asia/Karachi') returns a Date that has the Karachi year/month/day/hour/etc
 * but in the local JS Date object so you can call getDay(), setDate(), setHours(), etc reliably.
 */
const getTzDate = (timeZone = "Asia/Karachi", baseDate = new Date()) => {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });

  const parts = fmt.formatToParts(baseDate);
  const map = {};
  parts.forEach((p) => {
    if (p.type !== "literal") map[p.type] = p.value;
  });

  // build Date using the extracted components (local JS Date representing that wall-clock)
  return new Date(
    Number(map.year),
    Number(map.month) - 1,
    Number(map.day),
    Number(map.hour),
    Number(map.minute),
    Number(map.second)
  );
};

/**
 * Format any Date as 'YYYY-MM-DDTHH:mm' corresponding to the given timeZone's wall-clock.
 * This is suitable for <input type="datetime-local" /> values.
 */
const formatDateTimeForTimeZone = (date, timeZone = "Asia/Karachi") => {
  const fmt = new Intl.DateTimeFormat("en-GB", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  const parts = fmt.formatToParts(date);
  const map = {};
  parts.forEach((p) => {
    if (p.type !== "literal") map[p.type] = p.value;
  });

  return `${map.year}-${map.month}-${map.day}T${map.hour}:${map.minute}`;
};

const DateRangeSelector = ({ filters, setFilters }) => {
  const [range, setRange] = useState("Today");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customVisible, setCustomVisible] = useState(false);
  const TZ = "Asia/Karachi";

  useEffect(() => {
    setFilters({ ...filters, StartDate: fromDate, EndDate: toDate });
  }, [fromDate, toDate]);

  useEffect(() => {
    // tzNow is a Date representing Karachi wall-clock time
    const tzNow = getTzDate(TZ);
    let from = new Date(tzNow);
    let to = new Date(tzNow);

    switch (range) {
      case "Today": {
        from.setHours(0, 0, 0, 0);
        to.setHours(23, 59, 59, 999);
        break;
      }

      case "Last Day": {
        from.setDate(from.getDate() - 1);
        from.setHours(0, 0, 0, 0);
        to.setDate(to.getDate() - 1);
        to.setHours(23, 59, 59, 999);
        break;
      }

      case "This Week": {
        // day: 0=Sunday,1=Monday,...6=Saturday
        const day = from.getDay();
        // move back to Monday (if Sunday, go back 6 days)
        const diffToMonday = day === 0 ? -6 : 1 - day;
        from.setDate(from.getDate() + diffToMonday);
        from.setHours(0, 0, 0, 0);

        to = new Date(from);
        to.setDate(from.getDate() + 6);
        to.setHours(23, 59, 59, 999);
        break;
      }

      case "Last Week": {
        // Start from this week's Monday, then subtract 7 days to get last week's Monday
        const day = from.getDay();
        const diffToMonday = day === 0 ? -6 : 1 - day;
        const thisWeekStart = new Date(from);
        thisWeekStart.setDate(from.getDate() + diffToMonday);
        thisWeekStart.setHours(0, 0, 0, 0);

        const lastWeekStart = new Date(thisWeekStart);
        lastWeekStart.setDate(thisWeekStart.getDate() - 7);
        lastWeekStart.setHours(0, 0, 0, 0);

        const lastWeekEnd = new Date(lastWeekStart);
        lastWeekEnd.setDate(lastWeekStart.getDate() + 6);
        lastWeekEnd.setHours(23, 59, 59, 999);

        from = lastWeekStart;
        to = lastWeekEnd;
        break;
      }

      case "This Month": {
        from = new Date(tzNow.getFullYear(), tzNow.getMonth(), 1);
        from.setHours(0, 0, 0, 0);
        to = new Date(tzNow.getFullYear(), tzNow.getMonth() + 1, 0);
        to.setHours(23, 59, 59, 999);
        break;
      }

      case "Last Month": {
        from = new Date(tzNow.getFullYear(), tzNow.getMonth() - 1, 1);
        from.setHours(0, 0, 0, 0);
        to = new Date(tzNow.getFullYear(), tzNow.getMonth(), 0);
        to.setHours(23, 59, 59, 999);
        break;
      }

      case "Custom":
        setCustomVisible(true);
        return;

      default:
        break;
    }

    setFromDate(formatDateTimeForTimeZone(from, TZ));
    setToDate(formatDateTimeForTimeZone(to, TZ));
    setCustomVisible(false);
  }, [range]);


  const tzNow = getTzDate(TZ);
  // Compute 6 months back
  const sixMonthsBack = new Date(tzNow);

  sixMonthsBack.setMonth(sixMonthsBack.getMonth() - 6);

  // Format min date for the inputs
  const minDateTime = formatDateTimeForTimeZone(sixMonthsBack, TZ);
  const maxDateTime = formatDateTimeForTimeZone(tzNow, TZ);

  return (
    <div className="date-range-selector">
      <label className="form-label">Date Range</label>
      <select
        className="form-select mb-3"
        value={range}
        onChange={(e) => setRange(e.target.value)}
      >
        <option>Custom</option>
        <option>Today</option>
        <option>Last Day</option>
        <option>This Week</option>
        <option>Last Week</option>
        <option>This Month</option>
        <option>Last Month</option>
      </select>

      {range === "Custom" || customVisible ? (
        <div className="row g-3">
          <div className="col-md-6">
            <label>From:</label>
            <input
              type="datetime-local"
              className="form-control"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              min={minDateTime}      // ⬅️ BLOCK older dates
              max={maxDateTime}
            />
          </div>
          <div className="col-md-6">
            <label>To:</label>
            <input
              type="datetime-local"
              className="form-control"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              min={fromDate || minDateTime}  // ensures To >= From
              max={maxDateTime}
            />
          </div>
        </div>
      ) : (
        <div className="row g-3">
          <div className="col-md-6">
            <label>From:</label>
            <input
              type="datetime-local"
              className="form-control"
              value={fromDate}
              readOnly
            />
          </div>
          <div className="col-md-6">
            <label>To:</label>
            <input
              type="datetime-local"
              className="form-control"
              value={toDate}
              readOnly
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default DateRangeSelector;
